import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { mensajeDeError } from '../lib/mensajeDeError';

/**
 * QUÉ DÍAS DE LA SEMANA ENTRENAS.
 *
 * DE DÓNDE SALE: documento de Jesús del 5 de septiembre, ficha 06. En Mi semana, el día que
 * todavía no tenía dieta decía «Por decidir», porque hasta entonces el tipo del día se
 * elegía AL CREAR EL DÍA, con el interruptor de Entreno/Descanso de Nutrición: mientras
 * nadie creaba el día, nadie lo había dicho. Lo que pide es que se le PREGUNTE: «los siete
 * días marcables, y él los cambia cuando quiera». Con eso la app lo sabe de antemano.
 *
 * Y EL MOTIVO NO ES DE PANTALLA, ES DE COMIDA: el perientreno solo va los días de entreno,
 * así que dar por entrenado un martes que no entrena le mete el intra y el post de más.
 *
 * LA RESPUESTA ES LA QUE YA DA EN EL ALTA (`client_profiles.training_weekdays`, la pregunta
 * «¿Qué días de la semana tienes previsto entrenar?»). No se inventa un campo nuevo: se
 * reutiliza el que él ya contesta, y esto es lo que faltaba, poder cambiarlo después. Lo que
 * Jesús dice que NO sirve es otra cosa: «Entrena actualmente», texto libre que dice cuántas
 * veces y no qué días. Ése se queda como está, que es del entrenador.
 *
 * Se usa en dos sitios y es el mismo trozo en los dos: Mi perfil (donde vive el dato) y Mi
 * semana (donde se echa en falta).
 */

// Lunes primero, como la semana de la app y como `weekday()` en el servidor.
const DIAS = [
    { valor: 'lunes', corto: 'Lun', largo: 'Lunes' },
    { valor: 'martes', corto: 'Mar', largo: 'Martes' },
    { valor: 'miercoles', corto: 'Mié', largo: 'Miércoles' },
    { valor: 'jueves', corto: 'Jue', largo: 'Jueves' },
    { valor: 'viernes', corto: 'Vie', largo: 'Viernes' },
    { valor: 'sabado', corto: 'Sáb', largo: 'Sábado' },
    { valor: 'domingo', corto: 'Dom', largo: 'Domingo' },
];

// Lo guardado puede venir de dos sitios: del perfil como nombres («lunes») o de la semana
// como índices (0-6). Se admiten los dos y se sale siempre con nombres.
// Las tildes fuera antes de comparar: la ficha puede traer «miércoles» o «miercoles» según
// quién la escribiera, y comparar los dos da falso.
const sinTilde = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

const aNombres = (marcados) => {
    if (!Array.isArray(marcados)) return null;
    const nombres = marcados
        .map((d) => (typeof d === 'number'
            ? (DIAS[d] || {}).valor
            : (DIAS.find((x) => x.valor === sinTilde(d)) || {}).valor))
        .filter(Boolean);
    // Sin ningún día reconocible es que no ha contestado: en la base quedan fichas heredadas
    // de Calma con números sin significado verificable, y no se dan por respuesta.
    return nombres.length ? nombres : null;
};

const DiasQueEntrenas = ({ marcados, onGuardado }) => {
    const { api, refreshProfile } = useAuth();
    const guardados = aNombres(marcados);
    const [elegidos, setElegidos] = useState(guardados || []);
    const [guardando, setGuardando] = useState(false);
    // Si ha tocado los botones, aunque el resultado sea «ninguno». Sin esto, el que no ha
    // contestado nunca no puede decir «no entreno ningún día»: marca y desmarca, se queda
    // igual que estaba, y el botón de guardar no aparece.
    const [tocado, setTocado] = useState(false);

    // El perfil (o la semana) llega después del primer pintado: cuando aparece, se siembra.
    useEffect(() => { setElegidos(aNombres(marcados) || []); setTocado(false); }, [marcados]);

    const cambiado = JSON.stringify(elegidos) !== JSON.stringify(guardados || []);

    const alternar = (valor) => { setTocado(true); return setElegidos((antes) => (
        antes.includes(valor) ? antes.filter((d) => d !== valor)
            // En orden de semana siempre, se marquen como se marquen.
            : DIAS.map((d) => d.valor).filter((d) => antes.includes(d) || d === valor)
    )); };

    // «NO ENTRENO NINGÚN DÍA» TAMBIÉN ES UNA RESPUESTA (Jesús, 11-09).
    //
    // Aquí había un freno -- «marca al menos un día para poder guardarlo» -- que contradecía
    // al texto de dos líneas más abajo: «los días que no marques los tomamos como descanso».
    // El que no entrena ningún día no podía decirlo, así que se quedaba en «no ha
    // contestado» y la app le daba los cinco laborables como entreno. Su frase: «el perfil
    // promete una cosa y la app hace la contraria».
    const guardar = async () => {
        setGuardando(true);
        try {
            await api.put('/clients/profile', { training_weekdays: elegidos });
            if (refreshProfile) await refreshProfile();
            toast.success(elegidos.length
                ? 'Guardado. Ya sabemos qué días entrenas.'
                : 'Guardado. Tomamos la semana entera como descanso.');
            if (onGuardado) onGuardado(elegidos);
        } catch (e) {
            toast.error(mensajeDeError(e, 'No hemos podido guardar tus días. Inténtalo en un momento.'));
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div data-testid="dias-entreno">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Días que entrenas">
                {DIAS.map((d) => {
                    const activo = elegidos.includes(d.valor);
                    return (
                        <button
                            key={d.valor} type="button" aria-pressed={activo} aria-label={d.largo}
                            data-testid={`dia-entreno-${d.valor}`}
                            onClick={() => alternar(d.valor)}
                            className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-colors
                                ${activo
                                    ? 'bg-brand text-white border-brand'
                                    : 'bg-muted text-muted-foreground border-border hover:text-foreground'}`}>
                            {d.corto}
                        </button>
                    );
                })}
            </div>
            {(cambiado || (tocado && !elegidos.length)) && (
                <Button onClick={guardar} disabled={guardando} data-testid="guardar-dias-entreno"
                    className="mt-3 bg-brand hover:bg-brand/90 text-white">
                    {guardando ? 'Guardando…' : 'Guardar mis días'}
                </Button>
            )}
        </div>
    );
};

export default DiasQueEntrenas;
